import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, useAuth } from "@/lib/auth";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PortalShell } from "@/components/account/PortalShell";
import { fmtLocal, userTz } from "@/lib/time";
import {
    ArrowLeft, Mail, Plus, Loader2, Search, X, Paperclip, Send, Lock,
    CheckCircle2, XCircle, Building2, Megaphone, Download, Zap, Trash2,
} from "lucide-react";

const AutomationsModal = ({ tz, onClose }) => {
    const [data, setData] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [form, setForm] = useState({ name: "", trigger: "", template_key: "" });
    const [busy, setBusy] = useState(false);
    const load = () => api.get("/comms/automations").then(({ data }) => setData(data)).catch(() => setData({ rules: [], triggers: [], log: [] }));
    useEffect(() => {
        load();
        api.get("/comms/templates").then(({ data }) => setTemplates(data.templates.filter((t) => t.key !== "otp"))).catch(() => {});
    }, []);
    const create = async () => {
        setBusy(true);
        try {
            const trig = data.triggers.find((t) => t.key === form.trigger);
            const tpl = templates.find((t) => t.key === form.template_key);
            await api.post("/comms/automations", {
                ...form,
                name: form.name.trim() || `${trig?.label || form.trigger} → ${tpl?.name || form.template_key}`,
            });
            toast.success("Automation rule created.");
            setForm({ name: "", trigger: "", template_key: "" });
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not create rule."); }
        finally { setBusy(false); }
    };
    const toggle = async (r) => {
        await api.patch(`/comms/automations/${r.id}`, { enabled: !r.enabled });
        load();
    };
    const remove = async (r) => {
        await api.delete(`/comms/automations/${r.id}`);
        toast.success("Rule deleted.");
        load();
    };
    return (
        <div className="fixed inset-0 z-[999] bg-black/70 flex items-center justify-center p-4 overflow-y-auto" data-testid="comms-automations-modal">
            <div className="bg-[#1B2E55] border border-white/10 p-8 max-w-2xl w-full my-8">
                <div className="flex items-center justify-between mb-6">
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00] inline-flex items-center gap-2"><Zap size={12} /> / Automation rules</span>
                    <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="comms-automations-close"><X size={16} /></button>
                </div>
                {!data ? <Loader2 size={16} className="animate-spin text-[#FF5A00]" /> : (
                    <>
                        <div className="border border-white/10 p-4 mb-6" data-testid="automation-create-form">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <select value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })} className={inputCls} data-testid="automation-trigger-select">
                                    <option value="">When… (pick a trigger)</option>
                                    {data.triggers.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                                </select>
                                <select value={form.template_key} onChange={(e) => setForm({ ...form, template_key: e.target.value })} className={inputCls} data-testid="automation-template-select">
                                    <option value="">Send… (pick a template)</option>
                                    {templates.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
                                </select>
                            </div>
                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Rule name (optional — auto-generated)" className={`${inputCls} mt-2`} data-testid="automation-name-input" />
                            <button onClick={create} disabled={busy || !form.trigger || !form.template_key} data-testid="automation-create-btn"
                                className="mt-3 inline-flex items-center gap-1.5 bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-50 text-white px-3 py-2 text-[10px] font-semibold uppercase tracking-widest">
                                <Plus size={11} /> Add rule
                            </button>
                            <p className="mt-2 text-[10px] text-slate-500">Emails go to the campaign's client, fire once per campaign, and land in this hub with full history.</p>
                        </div>
                        <div className="space-y-2 mb-6" data-testid="automation-rules-list">
                            {data.rules.length === 0 && <div className="text-xs text-slate-600">No rules yet — add one above, e.g. "Booking confirmed → Campaign summary".</div>}
                            {data.rules.map((r) => (
                                <div key={r.id} className="flex flex-wrap items-center gap-3 border border-white/10 px-3 py-2" data-testid={`automation-rule-row-${r.id}`}>
                                    <button onClick={() => toggle(r)} data-testid={`automation-toggle-${r.id}`}
                                        className={`w-9 h-5 rounded-full relative transition-colors ${r.enabled ? "bg-[#FF5A00]" : "bg-white/10"}`}>
                                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${r.enabled ? "left-[18px]" : "left-0.5"}`} />
                                    </button>
                                    <div className="flex-1 min-w-[160px]">
                                        <div className="text-sm text-white">{r.name}</div>
                                        <div className="text-[10px] font-mono-tech text-slate-500">on <span className="text-[#FF5A00]">{r.trigger}</span> → template <span className="text-slate-300">{r.template_key}</span></div>
                                    </div>
                                    <button onClick={() => remove(r)} data-testid={`automation-delete-${r.id}`} className="text-slate-500 hover:text-red-400"><Trash2 size={13} /></button>
                                </div>
                            ))}
                        </div>
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">Recent automation activity</div>
                        <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="automation-log">
                            {data.log.length === 0 && <div className="text-xs text-slate-600">Nothing sent yet.</div>}
                            {data.log.map((l) => (
                                <div key={l.id} className="flex flex-wrap items-center gap-2 text-[11px] border border-white/10 px-3 py-1.5" data-testid={`automation-log-row-${l.id}`}>
                                    <DeliveryChip status={l.status} />
                                    <span className="text-white">{l.rule_name}</span>
                                    <span className="text-slate-500">→ {l.to}</span>
                                    <span className="text-slate-600 font-mono-tech ml-auto">{fmtLocal(l.at, tz)}</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const inputCls = "w-full bg-[#1B2E55] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-600";
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${(n / 1024).toFixed(0)} KB`);

const DeliveryChip = ({ status }) => {
    if (status === "internal") return <span className="inline-flex items-center gap-1 text-[9px] font-mono-tech uppercase tracking-widest text-slate-400 border border-white/15 px-1.5 py-0.5"><Lock size={9} /> internal</span>;
    if (status === "sent") return <span className="inline-flex items-center gap-1 text-[9px] font-mono-tech uppercase tracking-widest text-emerald-300 border border-emerald-400/40 px-1.5 py-0.5"><CheckCircle2 size={9} /> sent</span>;
    return <span className="inline-flex items-center gap-1 text-[9px] font-mono-tech uppercase tracking-widest text-red-300 border border-red-400/40 px-1.5 py-0.5"><XCircle size={9} /> failed</span>;
};

const AttachmentPicker = ({ attachments, setAttachments }) => {
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const onFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        for (const f of files) {
            if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} exceeds the 10 MB limit.`); continue; }
            setBusy(true);
            try {
                const b64 = await new Promise((res, rej) => {
                    const r = new FileReader();
                    r.onload = () => res(r.result.split(",")[1]);
                    r.onerror = rej;
                    r.readAsDataURL(f);
                });
                const { data } = await api.post("/comms/attachments", { filename: f.name, content_type: f.type || "application/octet-stream", content_b64: b64 });
                setAttachments((a) => [...a, data]);
            } catch (err) { toast.error(err.response?.data?.detail || `Could not upload ${f.name}.`); }
            finally { setBusy(false); }
        }
    };
    return (
        <div>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={onFiles} data-testid="comms-file-input" />
            <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="comms-attach-btn"
                    className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/40 text-slate-300 px-3 py-1.5 text-[10px] uppercase tracking-widest">
                    {busy ? <Loader2 size={11} className="animate-spin" /> : <Paperclip size={11} />} Attach
                </button>
                {attachments.map((a) => (
                    <span key={a.id} className="inline-flex items-center gap-1.5 border border-white/10 px-2 py-1 text-[10px] text-slate-300" data-testid={`comms-attachment-chip-${a.id}`}>
                        {a.name} <span className="text-slate-600">{kb(a.size)}</span>
                        <button onClick={() => setAttachments((x) => x.filter((y) => y.id !== a.id))} className="text-slate-500 hover:text-red-400"><X size={10} /></button>
                    </span>
                ))}
            </div>
        </div>
    );
};

const ComposeModal = ({ defaults, onClose, onSent }) => {
    const [kind, setKind] = useState(defaults?.kind || "external");
    const [to, setTo] = useState(defaults?.to || "");
    const [cc, setCc] = useState("");
    const [subject, setSubject] = useState(defaults?.subject || "");
    const [body, setBody] = useState("");
    const [templates, setTemplates] = useState([]);
    const [templateKey, setTemplateKey] = useState("");
    const [tplValues, setTplValues] = useState({});
    const [attachments, setAttachments] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [companyId, setCompanyId] = useState(defaults?.companyId || "");
    const [campaignId, setCampaignId] = useState(defaults?.campaignId || "");
    const [sending, setSending] = useState(false);

    useEffect(() => {
        api.get("/comms/templates").then(({ data }) => setTemplates(data.templates)).catch(() => {});
        api.get("/crm/companies").then(({ data }) => setCompanies(data.companies)).catch(() => {});
        api.get("/campaigns").then(({ data }) => setCampaigns(data)).catch(() => {});
    }, []);

    const tpl = templates.find((t) => t.key === templateKey);
    const send = async () => {
        setSending(true);
        try {
            const payload = {
                kind,
                to: kind === "external" ? to.split(",").map((s) => s.trim()).filter(Boolean) : [],
                cc: kind === "external" ? cc.split(",").map((s) => s.trim()).filter(Boolean) : [],
                subject,
                body_text: templateKey ? null : body,
                template_key: templateKey || null,
                template_values: tplValues,
                company_id: companyId || null,
                campaign_id: campaignId || null,
                thread_id: defaults?.threadId || null,
                attachment_ids: attachments.map((a) => a.id),
            };
            const { data } = await api.post("/comms/send", payload);
            toast.success(data.status === "sent" ? "Email sent." : data.status === "internal" ? "Internal message posted." : "Send failed — recorded as failed.");
            onSent(data);
        } catch (e) { toast.error(e.response?.data?.detail || "Could not send."); }
        finally { setSending(false); }
    };

    const canSend = subject.trim() && (templateKey || body.trim()) && (kind === "internal" || to.trim());
    return (
        <div className="fixed inset-0 z-[999] bg-black/70 flex items-center justify-center p-4 overflow-y-auto" data-testid="comms-compose-modal">
            <div className="bg-[#1B2E55] border border-white/10 p-8 max-w-2xl w-full my-8">
                <div className="flex items-center justify-between mb-6">
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">/ {defaults?.threadId ? "Reply" : "New message"}</span>
                    <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="comms-compose-close"><X size={16} /></button>
                </div>
                {!defaults?.threadId && (
                    <div className="flex gap-2 mb-4">
                        {["external", "internal"].map((k) => (
                            <button key={k} onClick={() => setKind(k)} data-testid={`comms-kind-${k}`}
                                className={`px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-widest border ${kind === k ? "border-[#FF5A00] text-white" : "border-white/10 text-slate-500"}`}>
                                {k === "external" ? "External email" : "Internal only"}
                            </button>
                        ))}
                    </div>
                )}
                <div className="space-y-3">
                    {kind === "external" && (
                        <>
                            <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="To — comma-separated emails" className={inputCls} data-testid="comms-to-input" />
                            <input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Cc (optional)" className={inputCls} data-testid="comms-cc-input" />
                        </>
                    )}
                    <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className={inputCls} data-testid="comms-subject-input" />
                    {kind === "external" && !defaults?.threadId && (
                        <select value={templateKey} onChange={(e) => { setTemplateKey(e.target.value); setTplValues({}); }} className={inputCls} data-testid="comms-template-select">
                            <option value="">No template — write your own message</option>
                            {templates.map((t) => <option key={t.key} value={t.key}>{`Template: ${t.name}`}</option>)}
                        </select>
                    )}
                    {tpl ? (
                        <div className="border border-white/10 p-3 space-y-2" data-testid="comms-template-vars">
                            <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500">Template variables</div>
                            {tpl.variables.map((v) => (
                                <input key={v.name} placeholder={`${v.name} — ${v.description}`}
                                    value={tplValues[v.name] || ""} data-testid={`comms-tplvar-${v.name}`}
                                    onChange={(e) => setTplValues({ ...tplValues, [v.name]: e.target.value })}
                                    className={inputCls} />
                            ))}
                        </div>
                    ) : (
                        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6}
                            placeholder={kind === "external" ? "Message — sent inside the branded NOXN email shell…" : "Internal note for the team…"}
                            className={inputCls} data-testid="comms-body-input" />
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className={inputCls} data-testid="comms-company-select">
                            <option value="">Link company (auto by email match)</option>
                            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className={inputCls} data-testid="comms-campaign-select">
                            <option value="">Link campaign (optional)</option>
                            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.business_name}</option>)}
                        </select>
                    </div>
                    {kind === "external" && <AttachmentPicker attachments={attachments} setAttachments={setAttachments} />}
                </div>
                <button onClick={send} disabled={sending || !canSend} data-testid="comms-send-btn"
                    className="mt-6 w-full bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-50 text-white py-3 text-xs font-semibold uppercase tracking-widest inline-flex items-center justify-center gap-2">
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={13} />}
                    {kind === "external" ? "Send email" : "Post internal message"}
                </button>
            </div>
        </div>
    );
};

const ThreadView = ({ threadId, tz, onReply }) => {
    const [data, setData] = useState(null);
    useEffect(() => {
        setData(null);
        api.get(`/comms/threads/${threadId}`).then(({ data }) => setData(data)).catch(() => setData({ thread: {}, messages: [] }));
    }, [threadId]);
    if (!data) return <Loader2 size={16} className="animate-spin text-[#FF5A00]" />;
    const last = data.messages[data.messages.length - 1];
    return (
        <div data-testid="comms-thread-view">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                    <h2 className="font-display font-black tracking-tighter text-2xl" data-testid="comms-thread-subject">{data.thread.subject}</h2>
                    <div className="mt-1 flex flex-wrap gap-3 text-[10px] font-mono-tech text-slate-500">
                        {data.thread.company_id && <span className="inline-flex items-center gap-1"><Building2 size={10} className="text-[#FF5A00]" /> linked company</span>}
                        {data.thread.campaign_id && <span className="inline-flex items-center gap-1"><Megaphone size={10} className="text-[#FF5A00]" /> linked campaign</span>}
                        <span>{data.messages.length} message{data.messages.length === 1 ? "" : "s"}</span>
                    </div>
                </div>
                <button onClick={() => onReply(data.thread, last)} data-testid="comms-reply-btn"
                    className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-4 py-2 text-[10px] font-semibold uppercase tracking-widest">
                    <Send size={11} /> Reply
                </button>
            </div>
            <div className="space-y-3">
                {data.messages.map((m) => (
                    <div key={m.id} className="border border-white/10 p-4" data-testid={`comms-message-${m.id}`}>
                        <div className="flex flex-wrap items-center gap-3 text-[10px] font-mono-tech text-slate-500">
                            <span className="text-white">{m.sent_by_name}</span>
                            {m.kind === "external" && <span>→ {m.to_emails.join(", ")}</span>}
                            {m.cc_emails?.length > 0 && <span>cc {m.cc_emails.join(", ")}</span>}
                            <span>{fmtLocal(m.at, tz)}</span>
                            <DeliveryChip status={m.status} />
                            {m.template_key && <span className="border border-white/15 px-1.5 py-0.5 uppercase tracking-widest">tpl: {m.template_key}</span>}
                        </div>
                        <div className="mt-2 text-sm text-slate-300 whitespace-pre-wrap">{m.body_preview}</div>
                        {m.attachments?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {m.attachments.map((a) => (
                                    <a key={a.id} href={`${api.defaults.baseURL}/comms/attachments/${a.id}/download`}
                                        target="_blank" rel="noreferrer" data-testid={`comms-msg-attachment-${a.id}`}
                                        className="inline-flex items-center gap-1.5 border border-white/10 px-2 py-1 text-[10px] text-slate-300 hover:border-[#FF5A00]/50">
                                        <Download size={10} className="text-[#FF5A00]" /> {a.name} <span className="text-slate-600">{kb(a.size)}</span>
                                    </a>
                                ))}
                            </div>
                        )}
                        {m.provider_id && m.status === "sent" && (
                            <div className="mt-2 text-[9px] font-mono-tech text-slate-600">Delivered to relay · id {String(m.provider_id).slice(0, 18)}</div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default function Comms() {
    usePageMeta({ title: "Communications · NOXN", description: "Email hub and internal messaging." });
    const { user } = useAuth();
    const tz = userTz(user);
    const [params] = useSearchParams();
    const [threads, setThreads] = useState(null);
    const [kind, setKind] = useState("");
    const [q, setQ] = useState("");
    const [companyFilter, setCompanyFilter] = useState(params.get("company") || "");
    const [companies, setCompanies] = useState([]);
    const [selected, setSelected] = useState(null);
    const [compose, setCompose] = useState(null);
    const [autoOpen, setAutoOpen] = useState(false);

    const load = () => {
        const p = {};
        if (kind) p.kind = kind;
        if (q.trim()) p.q = q.trim();
        if (companyFilter) p.company_id = companyFilter;
        api.get("/comms/threads", { params: p }).then(({ data }) => setThreads(data.threads)).catch(() => setThreads([]));
    };
    useEffect(() => { load(); }, [kind, q, companyFilter]);
    useEffect(() => { api.get("/crm/companies").then(({ data }) => setCompanies(data.companies)).catch(() => {}); }, []);

    const openReply = (thread, lastMsg) => setCompose({
        threadId: thread.id,
        kind: thread.kind,
        subject: thread.subject?.startsWith("Re:") ? thread.subject : `Re: ${thread.subject}`,
        to: (lastMsg?.to_emails || []).join(", "),
        companyId: thread.company_id || "",
        campaignId: thread.campaign_id || "",
    });

    return (
        <PortalShell title="Comms" mainClassName="p-0">
        <div className="min-h-screen bg-[#0F1E3B] text-white" data-testid="comms-page">
            <main className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <Mail size={16} className="text-[#FF5A00]" />
                            <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-slate-400">Email hub · sends from NOXN</span>
                        </div>
                        <h1 className="font-display font-black tracking-tighter text-4xl">Communications.</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setAutoOpen(true)} data-testid="comms-automations-btn"
                            className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FF5A00]/60 text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors">
                            <Zap size={13} className="text-[#FF5A00]" /> Automations
                        </button>
                        <button onClick={() => setCompose({})} data-testid="comms-compose-btn"
                            className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest">
                            <Plus size={13} /> Compose
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8">
                    <div>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {[["", "All"], ["external", "External"], ["internal", "Internal"]].map(([v, l]) => (
                                <button key={v} onClick={() => setKind(v)} data-testid={`comms-filter-${l.toLowerCase()}`}
                                    className={`px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-widest border ${kind === v ? "border-[#FF5A00] text-white" : "border-white/10 text-slate-500"}`}>{l}</button>
                            ))}
                        </div>
                        <div className="relative mb-2">
                            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subjects…" data-testid="comms-search-input"
                                className="w-full bg-[#1B2E55] border border-white/10 pl-9 pr-3 py-2 text-sm text-white placeholder:text-slate-600" />
                        </div>
                        <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} className={`${inputCls} mb-3`} data-testid="comms-company-filter">
                            <option value="">All companies</option>
                            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        {!threads ? <Loader2 size={16} className="animate-spin text-[#FF5A00]" /> :
                            threads.length === 0 ? (
                                <div className="border border-white/10 p-6 text-center text-xs text-slate-500" data-testid="comms-empty-state">
                                    No conversations yet — hit Compose to send your first email or internal message.
                                </div>
                            ) : (
                                <div className="border border-white/10 divide-y divide-white/5 max-h-[60vh] overflow-y-auto" data-testid="comms-threads-list">
                                    {threads.map((t) => (
                                        <button key={t.id} onClick={() => setSelected(t.id)} data-testid={`comms-thread-row-${t.id}`}
                                            className={`w-full text-left px-4 py-3 hover:bg-white/[0.03] transition-colors ${selected === t.id ? "bg-white/[0.05]" : ""}`}>
                                            <div className="flex items-center gap-2">
                                                <span className="flex-1 text-sm text-white truncate">{t.subject}</span>
                                                <DeliveryChip status={t.last_status || (t.kind === "internal" ? "internal" : "sent")} />
                                            </div>
                                            <div className="mt-1 text-[10px] font-mono-tech text-slate-500 truncate">
                                                {[t.company_name, t.campaign_name].filter(Boolean).join(" · ") || "unlinked"} · {t.message_count} msg · {fmtLocal(t.last_at, tz)}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                    </div>
                    <div>
                        {selected ? (
                            <ThreadView threadId={selected} tz={tz} onReply={openReply} />
                        ) : (
                            <div className="border border-white/10 p-14 text-center text-sm text-slate-500" data-testid="comms-no-selection">
                                Select a conversation to read the full threaded history.
                            </div>
                        )}
                    </div>
                </div>
            </main>
            {compose && (
                <ComposeModal defaults={compose} onClose={() => setCompose(null)}
                    onSent={(m) => { setCompose(null); load(); setSelected(m.thread_id); }} />
            )}
            {autoOpen && <AutomationsModal tz={tz} onClose={() => { setAutoOpen(false); load(); }} />}
        </div>
        </PortalShell>
    );
}
